import React, { useEffect, useState } from "react";
import { DataGrid, Popup } from "devextreme-react";
import { Button } from "devextreme-react/button";
import { Column, FilterRow, HeaderFilter, Pager, Paging, Sorting } from "devextreme-react/data-grid";

import { ReviewNewChargesApi } from "../../../../shared/api/ReviewNewCharges.api";
import { FieldKeyValues, GridColumnNames, Pages } from "../../../../shared/constants";
import { SurchargeEventCalculationResult } from "../../../../shared/models/SurchargeEventCalculationResult";
import DataGridCustomizeCell from "./DataGridCustomizeCell";
import SurchargeCustomerEditForm from "./EditForm";
import { SurchargeDetailForEdit } from "../../../../shared/models/SurchargeDetailForEdit";
import "./style.css";
import SurchargeSelectBox from "./SurchargeSelectBox";
import { useSurchargeSelectBoxState } from "@/hooks/useSurchargeSelectBoxState";
import { calculationResultColumns } from "@/shared/business/ReviewNewCharges";
import { usePopupDisplay } from "@/hooks/usePopupDisplay";
import NextButton from "@/components/NextButton";
import { Utilities } from "@/shared/utils/utilities";
import { store } from "@/store/store";
import { setToastInfo } from "@/redux/shareSlice";

const ReviewNewCharges: React.FC = () => {
  const { popupVisible, showPopUp, hidePopup } = usePopupDisplay();

  const {
    calculatedEvents,
    selectedSurchargeEvent,
    handleSurchargeEventChange,
    calculatedEventRuns,
    selectedCalculatedEventRun,
    handleSurchargeEventRunChange,
    processSurchargeEventChanged,
  } = useSurchargeSelectBoxState();

  const [calculatedResults, setCalculatedResults] = useState([] as SurchargeEventCalculationResult[]);

  // const [currentSelectedCalculatedEventRunId, setCurrentSelectedCalculatedEventRunId] = useState("");
  const [formEditData, setFormEditData] = useState({} as SurchargeDetailForEdit);

  useEffect(() => {
    selectedCalculatedEventRun && handleSurchargeEventRunChanged(selectedCalculatedEventRun.id);
  }, [selectedCalculatedEventRun]);

  const handleSurchargeEventRunChanged = async (selectedCalculatedEventRunId: string) => {
    var data = await ReviewNewChargesApi.showCalculatedEventRun(selectedCalculatedEventRunId);
    if (!data) {
      return;
    }

    data.forEach((item) => (item.formulaNameAndVersion = item.formulaName + " - Version " + item.formulaVersionNumber));
    setCalculatedResults(data);
  };

  const handleApproveSurcharge = async () => {
    if (await ReviewNewChargesApi.approveSurcharge(selectedCalculatedEventRun.id)) {
      store.dispatch(setToastInfo(`All customers associated with this event have been approved.\r\n\r\nTo opt any customers out of this event, you can now do so individually.`));
      await processSurchargeEventChanged(selectedSurchargeEvent.id);
    }
  };

  const handleDownloadLog = async () => {
    var fileName = await ReviewNewChargesApi.getSurchargeEventCalculationLog(selectedCalculatedEventRun.id);
    !Utilities.isNullOrEmptyString(fileName) &&
      store.dispatch(setToastInfo(`Surcharge processing log is downloaded successfully as ${fileName}.`));
  };

  const handleDownloadLink = async () => {
    var fileName = await ReviewNewChargesApi.dumpSurchargeEventCalculationResults(selectedCalculatedEventRun.id);
    !Utilities.isNullOrEmptyString(fileName) &&
      store.dispatch(setToastInfo(`Screen data is downloaded successfully as ${fileName}.`));
  };

  const handleExportToSapFormat = async () => {
    var fileName = await ReviewNewChargesApi.exportToSapFormat(selectedCalculatedEventRun.id);
    !Utilities.isNullOrEmptyString(fileName) &&
      store.dispatch(setToastInfo(`Prices in sap load format is downloaded successfully as ${fileName}.`));
  };

  const handleRowClick = async (e: any) => {
    var editData = await ReviewNewChargesApi.surchargeReviewRetrieveForEdit(e.data.id);
    if (editData) {
      editData.surchargeEventCalculationResultId = e.data.id;
      setFormEditData(editData);
      showPopUp();
    }
  };

  const onRowPrepared = (e: any) => {
    if (e.rowType === "data") {
      e.rowElement.style.cursor = "pointer";
    }
  };

  const onCellPrepared = (e: any) => {
    if (!e || !e.data) {
      return;
    }

    const dataField = e.column.dataField;
    if (dataField === GridColumnNames.OldSurcharge || dataField === GridColumnNames.CalculatedSurcharge) {
      e.cellElement.style.backgroundColor = e.data.oldSurcharge > e.data.calculatedSurcharge ? "#ff3333" : "#b6f9a2";
    }
  };

  const handleFormClose = async (reloadData: boolean) => {
    hidePopup();
    if (reloadData) {
      await handleSurchargeEventRunChanged(selectedCalculatedEventRun.id);
    }
  };

  return (
    <>
      {selectedSurchargeEvent.surchargeType === FieldKeyValues.QuarterlySurcharge ? (
        <div className="warning-info">THIS IS FOR INFORMATION ONLY. DO NOT SEND IT TO SAP. CANNOT EXPORT DATA</div>
      ) : null}

      <h4>Review & Approve New Charges</h4>
      <h5>Calculation Event Selection</h5>
      <SurchargeSelectBox
        calculatedEvents={calculatedEvents}
        selectedSurchargeEvent={selectedSurchargeEvent}
        handleSurchargeEventChange={handleSurchargeEventChange}
        calculatedEventRuns={calculatedEventRuns}
        selectedCalculatedEventRun={selectedCalculatedEventRun}
        handleSurchargeEventRunChange={handleSurchargeEventRunChange}
      />

      {calculatedResults ? (
        <div>
          <h5>Actions</h5>
          {selectedCalculatedEventRun.approvedBy !== null ? (
            <div className="approved-surcharge-event">
              This surcharge was approved by {selectedCalculatedEventRun.approvedBy} at {new Date(selectedCalculatedEventRun.approvedAt).toLocaleDateString()}.
            </div>
          ) : null}

          <div className="mt-3">
            {selectedCalculatedEventRun.eligibleForApproval && !selectedCalculatedEventRun.approvedAt ? (
              <Button type="default" className="me-3" onClick={handleApproveSurcharge}>
                APPROVE SURCHARGE INCREASES
              </Button>
            ) : null}

            <Button type={selectedCalculatedEventRun.failureCount > 0 ? "danger" : "default"} className="me-3" onClick={handleDownloadLog}>
              VIEW SURCHARGE PROCESSING LOG
            </Button>

            {selectedCalculatedEventRun.approvedAt ? (
              <>
                <Button type="default" className="me-3" onClick={handleDownloadLink}>
                  EXPORT SCREEN DATA
                </Button>
                <Button type="default" className="me-3" onClick={handleExportToSapFormat}>
                  EXPORT PRICES IN SAP LOAD FORMAT
                </Button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      <h5 className="mt-4">Calculation Event Results</h5>
      <DataGrid
        dataSource={calculatedResults}
        showBorders={true}
        keyExpr="shipTo"
        focusedRowEnabled={true}
        repaintChangesOnly={true}
        allowColumnResizing={true}
        allowColumnReordering={true}
        hoverStateEnabled={true}
        wordWrapEnabled={true}
        columnAutoWidth={true}
        onRowClick={handleRowClick}
        onRowPrepared={onRowPrepared}
        onCellPrepared={onCellPrepared}
      >
        <FilterRow visible={true} />
        <HeaderFilter visible={true} />
        <Sorting mode="multiple" />
        <Paging enabled={true} defaultPageSize={10} />
        <Pager
          visible={true}
          allowedPageSizes={[10, 25, 50, 75]} // Options for page size
          showPageSizeSelector={true}
          showInfo={true} // Show "Page X of Y"
          showNavigationButtons={true}
        />

        {calculationResultColumns.map((column, index) => (
          <Column key={index} dataField={column.dataField} caption={column.caption} cellRender={(e) => DataGridCustomizeCell(e)}></Column>
        ))}
      </DataGrid>

      <div className="mt-2 button-height">
        <NextButton nextPage={Pages.ReviewEmails} />
      </div>

      {popupVisible && (
        <SurchargeCustomerEditForm visible={popupVisible} closeForm={handleFormClose} surchargeDetail={formEditData}></SurchargeCustomerEditForm>
      )}
    </>
  );
};

export default ReviewNewCharges;
